import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children"],
  _excluded2 = ["checked", "type"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import ReactLinkify from 'react-linkify';
import ReactAutoLink from 'react-autolink';
import ReactMarkdown from 'react-markdown';
import { fromJS, is, List, Map } from 'immutable';
import moment from 'moment';
import remarkGfm from 'remark-gfm';
import remarkLinkify from 'remark-linkify';
import EmojiRegex from 'emoji-regex';
import { getBrand } from 'flow/brands/brands';
import CodeBlockWithCopy from 'flow/components/CodeBlockWithCopy';
import Checkbox from 'flow/components/ui-components/Checkbox';
import MessageMention from 'flow/pages/messages/MessageMention';
import ChatMessageType from 'flow/types/chat-message-type';
import { extractAbbreviatedName, extractName, getColorFromInt, homeActionUrlPath, isEmptyNullOrUndefined, listToMap } from 'flow/utils';
import { jsx as _jsx } from "react/jsx-runtime";
export var SCROLL_TO_BOTTOM_MARGIN = 150;
export var MAX_DISPLAYED_MESSAGES = 50;
export var MAX_ALLOWED_ATTACHMENT_SIZE = 20 * 1024 * 1024;

// Regexp that matches start of string or white space char, followed by '@' and one or more characters
// that are not '@', and then end of string ($)
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// @        matches a '@'-character
// [^@]+    matches anything that is not a '@'-character, one or more times
// $        matches the end of input)
export var REGEXP_POSSIBLE_MENTION_STRICT = '(?:^|\\s)(@[^@]+$)';

// Regexp that matches start of string or white space char, followed by '@' and zero or more characters
// that are not '@', and then end of string ($)
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// @        matches a '@'-character
// [^@]*    matches anything that is not a '@'-character, zero or more times
// $        matches the end of input)
export var REGEXP_POSSIBLE_MENTION = '(?:^|\\s)(@[^@]*$)';

// Regexp that matches start of string or white space char, followed by '/' and zero or more characters
// that are not '/'
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// /        matches a '/'-character
// [^/]*    matches anything that is not a '/'-character, zero or more times
// )        end of non-capturing match
export var REGEXP_POSSIBLE_PREMADE_ANSWER = '(?:^|\\s)/([^/]+)';

// Regexp that matches start of string or white space char, followed by ':' and two or more characters
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// :        matches a ':'-character
// [^:]*    matches anything that is not a ':'-character, zero or more times
// $        matches the end of input)
export var REGEXP_POSSIBLE_EMOJI = '(?:^|\\s)(:[^:]*$)';

// This regexp matches one or more whitespace characters
var REGEXP_WHITESPACE_CHARS = /[\s]+/;
var MESSAGE_MAX_VISIBLE_LENGTH = 300;
export var tabKey = 9;
export var enterKey = 13;
export var escaperKey = 27;
export var arrowUpKey = 38;
export var arrowDownKey = 40;
export var shouldSendMessageOnInput = function shouldSendMessageOnInput(charCode) {
  var event = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return charCode === enterKey && !event.shiftKey && !event.ctrlKey;
};
export var applyLink = function applyLink(text) {
  return ReactAutoLink.autolink(text, {
    target: '_blank',
    rel: 'nofollow noopener noreferrer',
    "class": 'hyphens-auto'
  });
};
var applyFormat = function applyFormat(format, text, sessionKey) {
  if (format !== null && format !== void 0 && format.has('chatUser')) {
    return /*#__PURE__*/_jsx(MessageMention, {
      name: text,
      chatUserKey: format.getIn(['chatUser', 'key']),
      sessionKey: sessionKey
    });
  }
  // Apply link processing for non-mention text when markdown is disabled
  return /*#__PURE__*/_jsx(ReactLinkify, {
    properties: {
      target: '_blank',
      rel: 'nofollow noopener noreferrer',
      className: 'text-link'
    },
    children: text
  });
};

/**
 * Non-formatted styling.
 *
 * @param startPosition the start of the 'no format'-styling
 * @param length the length of the 'no format'-styling
 * @return the 'no format'-styling
 */
var createStyling = function createStyling(startPosition, length) {
  return fromJS({
    startPosition: startPosition,
    length: length,
    stylingReference: null
  });
};

/**
 * Take the supplied list of stylings and interleave the implicit spacing
 * in format with explicit stylings.
 *
 * This method is _only_ made public (exported) for testability, and should
 * _not_ be used directly.
 *
 * @param stylings a List of formatDTOs
 * @param length the length of the text to be formatted
 * @return a List of format objects. Unformatted will have stylingReference set to 'null'
 * {
 *   startPosition: <number>,
 *   length: <number>,
 *   stylingReference: Object
 * }
 */
export var stylingGapFill = function stylingGapFill(stylings, length) {
  // Only one 'no-format'-format if list of stylings is empty
  if (!stylings || stylings.isEmpty()) {
    return List.of(createStyling(0, length));
  } else {
    // Sort stylings by startPosition to ensure correct gap filling
    var sortedStylings = stylings.sortBy(function (s) {
      return s.get('startPosition');
    });
    // Initial reducer state.
    // 'startPosition' is a moving index to ensure that there are no gaps in the list of stylings.
    var accumulator = fromJS({
      startPosition: 0,
      data: []
    });

    // The resulting list will contain all stylings save for a trailing one
    var gapFilled = sortedStylings.reduce(function (acc, format) {
      var fmtOffset = format.get('startPosition');
      var fmtLength = format.get('length');
      var accOffset = acc.get('startPosition');
      return acc.set('startPosition', fmtOffset + fmtLength).update('data', function (prev) {
        return prev.push(createStyling(accOffset, fmtOffset - accOffset)).push(format);
      });
    }, accumulator).get('data');
    var last = gapFilled.last();
    var startPosition = last.get('startPosition') + last.get('length');

    // Add a 'no-format'-format at the end and filter out all 0-length stylings.
    return gapFilled.push(createStyling(startPosition, length - startPosition)).filter(function (f) {
      return f.get('length') > 0;
    });
  }
};

/**
 * Apply the list of stylings to the supplied text.
 * The stylings are expected to be non-overlapping.
 *
 * @param messageText the format-bare text
 * @param stylings a List of StylingDTOs
 * @param sessionKey string resembling a chatsessionKey
 * @return React component containing formatted text
 */
export var applyStyling = function applyStyling(messageText) {
  var stylings = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : List();
  var sessionKey = arguments.length > 2 ? arguments[2] : undefined;
  return applyStylingInternal(messageText, stylings, sessionKey, applyFormat);
};
var applyStylingInternal = function applyStylingInternal(messageText, stylings, sessionKey, formatApplicator) {
  var gapFilled = stylingGapFill(stylings, messageText.length);
  var styled = gapFilled.reduce(function (acc, styling) {
    var startPosition = styling.get('startPosition');
    var length = styling.get('length');
    var element = formatApplicator(styling.get('stylingReference'), messageText.substr(startPosition, length), sessionKey);
    return acc.push(element);
  }, List());
  return React.Children.toArray(styled.toArray());
};

/**
 * For each text element in the given list it extracts all emojis
 * and creates individual elements out of them, through the help of
 * the given emojiBuilder function.
 *
 * @param messageText  the original list of text (or other parts)
 * @param emojiBuilder a function to create emojis takes a string, returns what ever you want to show
 * @return a list of text with emojis extracted and recreated according to emojiBuilder
 */
export var buildEmojiMessage = function buildEmojiMessage(messageText, emojiBuilder) {
  // Defensive: handle string, array, or React element input
  var text = '';
  if (typeof messageText === 'string') {
    text = messageText;
  } else if (Array.isArray(messageText) && messageText.length > 0) {
    var first = messageText[0];
    if (first && typeof first === 'string') {
      text = first;
    } else if (first && first.props && typeof first.props.children === 'string') {
      text = first.props.children;
    } else if (first && first.props && Array.isArray(first.props.children)) {
      text = first.props.children.join('');
    }
  } else if (messageText && messageText.props && typeof messageText.props.children === 'string') {
    text = messageText.props.children;
  }
  var result = [];
  var emojiRegex = EmojiRegex();
  for (var match = emojiRegex.exec(text); match !== null; match = emojiRegex.exec(text)) {
    var emoji = match[0];
    result.push(emojiBuilder(emoji));
  }
  return result;
};

/**
 * Checks if a string is only containing emojis
 * and is very short.
 * @param text a string
 * @return true or false.
 */
export var isShortEmojiMessage = function isShortEmojiMessage(text) {
  var regex = EmojiRegex();
  var emojiCount = 0;
  // Since emoji can be made out of multiple code points
  // we rebuild the message to check if it's only made up of emojis
  var emojiText = '';
  for (var match = regex.exec(text); match !== null; match = regex.exec(text)) {
    emojiCount++;
    var emoji = match[0];
    emojiText += emoji;
  }

  // A short emoji message is a message where the text:
  // - is non empty text
  // - only containing emojis (and whitespaces)
  // - is less than four emojis
  var noSpacesText = text.replace(/\s+/g, '');
  return text.length !== 0 && noSpacesText === emojiText && emojiCount < 4;
};

/**
 * Removes variation selector unicode (UVS) characters from a string.
 *
 * Java 8 Webkit does not seem to be able to render the UVS.
 *
 * @param text a string that might contain variation selector characters
 * @return string without UVS
 */
//ToDo investigate if this function is needed in other browsers than Desktop's
// eslint-disable-next-line @typescript-eslint/no-unused-vars
var removeVariationSelector = function removeVariationSelector(text) {
  var result = '';
  if (text !== undefined && text !== null) {
    var EMOJI_VARIATION_SELECTOR = 'fe0f';
    for (var i = 0; i < text.length; i++) {
      if (text.charCodeAt(i).toString(16) !== EMOJI_VARIATION_SELECTOR) {
        result += text[i];
      }
    }
  }
  return result;
};

/**
 * Check if a chatsession contains a member with a mobileNumber set.
 * If so the member is listed as an 'external contact'.
 *
 * @param session a chatsession
 *
 * @return true if one of the members have mobileNumber
 */
export var isSMSChat = function isSMSChat(session) {
  if (!session) {
    return false;
  }
  return session.get('members', List()).some(function (member) {
    return member.has('mobileNumber');
  });
};
export var isSMSOnlySession = function isSMSOnlySession(session) {
  return isSMSChat(session) && session.get('members', List()).size === 2;
};

/**
 * Escapes special regexp characters in a string
 */
export var escapeRegExp = function escapeRegExp(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

/**
 * This method truncates words in such a way that the last word is not cut off.
 *
 * Example.
 *      Let the pipe character '|' denote where the cut happens.
 *      Suppose we're truncating a text with a long word straddling the cutting point:
 *          "[beginning of text] in a really long sentence withARe|allyLongWord at the cutting point [end of text]"
 *      The text this method returns will then end in the following:
 *          "[beginning of text] in a really long sentence withAReallyLongWord ..."
 */
export var truncateText = function truncateText(text) {
  var max_length = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MESSAGE_MAX_VISIBLE_LENGTH;
  if (!text) {
    return '';
  }
  if (text.length < max_length) {
    return text;
  }
  var remainingWords = text.slice(max_length).split(REGEXP_WHITESPACE_CHARS);
  var remainderOfLastWord = remainingWords[0];
  var truncatedText = text.slice(0, max_length).concat(remainderOfLastWord);
  if (remainingWords[1]) {
    // If there are even more words after the last bit of the cut-off word, then add an ellipsis to the end.
    return truncatedText.concat(' ').concat('...');
  } else {
    return truncatedText;
  }
};

/**
 * Help method to inform the message lists if the message should be presented as one item or not.
 *
 * @param prevMessage
 * @param nextMessage
 * @returns {boolean}
 */
var shouldGroupMessages = function shouldGroupMessages(prevMessage, nextMessage) {
  var prevMessageTime = moment(prevMessage.get('sent'));
  var nextMessageTime = moment(nextMessage.get('sent'));
  var prevMessageSender = prevMessage.getIn(['from', 'extensionKey']);
  var nextMessageSender = nextMessage.getIn(['from', 'extensionKey']);
  var prevMessageIsPost = ChatMessageType.get(prevMessage.get('type')) === ChatMessageType.POST;
  var nextMessageIsPost = ChatMessageType.get(nextMessage.get('type')) === ChatMessageType.POST;
  return !prevMessageIsPost && !nextMessageIsPost && prevMessageSender === nextMessageSender && nextMessageTime.diff(prevMessageTime, 'minutes') < 3;
};

/**
 * Group a list of messages according to the criteria shouldGroupMessages.
 *
 * @param groupedByDate list of messages.
 */
export var getMessageGroups = function getMessageGroups(groupedByDate) {
  return groupedByDate.map(function (dateGroup) {
    return dateGroup.reduce(function (acc, message) {
      if (acc.size > 0 && shouldGroupMessages(acc.last().last(), message)) {
        return acc.update(-1, function (lastList) {
          return lastList.push(message);
        });
      } else {
        return acc.push(List().push(message));
      }
    }, List());
  });
};
export var getWidgetChatSenderName = function getWidgetChatSenderName(message, extensionKey, i18next) {
  var from = message.get('from');
  if (messageIsFromExtension(message, extensionKey)) {
    return i18next.t('chat.you');
  }
  var senderContact = from.get('contact');
  return extractAbbreviatedName(senderContact, 'Visitor');
};
export var messageIsFromExtension = function messageIsFromExtension(message, extensionKey) {
  if (!message || !message.get('from') || !extensionKey) {
    return false;
  }
  var from = message.get('from');
  return extensionKey === from.get('extensionKey');
};

/**
 * Compares the age of message0 and message1. This function returns a negative
 * number, 0 or a positive number if message0 is older, the same message or
 * newer than message1, respectively.
 *
 * @param message0 the first message
 * @param message1 the second message
 * @return {number}
 */
export var compareChatMessageAge = function compareChatMessageAge(message0, message1) {
  /*
   * Please don't do this stuff, it's really dirty to extract IDs from entity
   * keys.
   *
   * @param key the chatmessage entity key
   * @return the id if the id is found, null if key is null or undefined, or
   *         undefined if the id couldn't be parsed
   */
  var extractChatMessageId = function extractChatMessageId(key) {
    if (key === undefined || key === null) {
      return null;
    }
    var matches = /^chatmessage-([0-9]+)$/.exec(key);
    return matches !== null ? parseInt(matches[1]) : undefined;
  };
  var message0Id = message0 ? extractChatMessageId(message0.get('key')) : null;
  var message1Id = message1 ? extractChatMessageId(message1.get('key')) : null;
  if (message0Id === undefined || message1Id === undefined) {
    throw new Error('Could not parse message id for one or both of the messages (' + message0Id + ', ' + message1Id + ')');
  } else if (message0Id === null && message1Id === null) {
    return 0;
  } else if (message0Id === null) {
    return 0 - message1Id;
  } else if (message1Id === null) {
    return message0Id;
  } else {
    return message0Id - message1Id;
  }
};
export var compareMessageListsLatestAge = function compareMessageListsLatestAge(prevMessages, nextMessages) {
  if (!prevMessages || !nextMessages || prevMessages.isEmpty() || nextMessages.isEmpty()) {
    return false;
  }
  var sortedOldMessages = prevMessages.sort(compareChatMessageAge);
  var sortedNewMessages = nextMessages.sort(compareChatMessageAge);
  var lastOldestMessage = sortedOldMessages.last();
  var newOldestMessage = sortedNewMessages.last();
  return compareChatMessageAge(lastOldestMessage, newOldestMessage) > 0;
};

/**
 * This method is for comparing the latest messages in a session's 'latestMessage' field,
 * and the latest message in that session's list of messages.
 *
 * @param chatsState    The state of the substore 'chats' of our Redux-store
 * @param sessionKey    The key of the chatsession we're looking at
 * @returns {*} A blob with some error information
 */
export var compareLatestMessageAndMessageList = function compareLatestMessageAndMessageList(chatsState, sessionKey) {
  var latestMessageOnSession = chatsState.getIn(['sessions', sessionKey, 'latestMessage']);
  var sessionMessageList = chatsState.getIn(['messages', sessionKey], List());
  var isMissingLatestMessage = !latestMessageOnSession;
  var isMissingMessagesList = sessionMessageList.isEmpty();
  var isKeyMismatch = false;
  var latestKeyOnSession = latestMessageOnSession ? latestMessageOnSession.get('key') : null;
  var latestMessageOnMessageList = sessionMessageList.max(compareChatMessageAge) || null;
  var latestKeyOnMessageList = latestMessageOnMessageList ? latestMessageOnMessageList.get('key') : null;
  if (latestKeyOnSession !== latestKeyOnMessageList) {
    isKeyMismatch = true;
  }
  return {
    isMissingLatestMessage: isMissingLatestMessage,
    isMissingMessagesList: isMissingMessagesList,
    isKeyMismatch: isKeyMismatch,
    latestKeyOnSession: latestKeyOnSession,
    latestKeyOnMessageList: latestKeyOnMessageList
  };
};

/**
 * Compare two messages (coming either from Top or from the API). If the second
 * argument is newer, return true to signal that we'd like to ask the API for
 * new messages.
 *
 * @param oldLatestMsg  A ChatMessage
 * @param newLatestMsg  Another ChatMessage, that is potentially newer than the first argument.
 * @returns {boolean} true if the first message is older than the second.
 */
export var shouldFetchCompleteMessages = function shouldFetchCompleteMessages(oldLatestMsg, newLatestMsg) {
  if (!oldLatestMsg && !newLatestMsg) {
    // No previous messages in this chat, and none received. Don't attempt to fetch more from the API.
    return false;
  }
  return compareChatMessageAge(oldLatestMsg, newLatestMsg) < 0;
};
export var mergeNewChatMessageList = function mergeNewChatMessageList(prevMessages, nextMessages) {
  var limitMessageAmount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  if (List.isList(prevMessages) && !List.isList(nextMessages)) {
    return prevMessages;
  } else if (!List.isList(prevMessages) && List.isList(nextMessages)) {
    return nextMessages;
  } else if (!List.isList(prevMessages) && !List.isList(nextMessages)) {
    return List();
  }
  var prevMessageMap = listToMap(prevMessages, function (e) {
    return e.get('key');
  });
  var nextMessageMap = listToMap(nextMessages, function (e) {
    return e.get('key');
  });
  // Keep the stored object whenever the incoming one says the same thing.
  // Immutable 3 does not compare before writing, so a plain merge would hand
  // back a new reference for every message the response repeats, and every
  // row that reads one by key would re-render for a message it did not care
  // about.
  var mergedMessageMap = prevMessageMap.withMutations(function (merged) {
    nextMessageMap.forEach(function (nextMessage, key) {
      if (!is(merged.get(key), nextMessage)) {
        merged.set(key, nextMessage);
      }
    });
  });
  // withMutations hands back the very same map when nothing was written, so
  // this is a reference check, not a walk. Returning the stored list keeps
  // its identity too - otherwise every merge produced a new List even when
  // it held exactly the same messages, and everything selecting the list
  // re-rendered for a response that changed nothing.
  if (mergedMessageMap === prevMessageMap) {
    return prevMessages;
  }
  return mergedMessageMap.toList().sort(compareChatMessageAge).update(function (newMessageList) {
    if (limitMessageAmount) {
      return newMessageList.takeLast(MAX_DISPLAYED_MESSAGES);
    } else {
      return newMessageList;
    }
  });
};

/**
 * True when a fetched page cannot be shown to continue the messages already
 * stored, so that merging the two would leave a hole in the middle of the chat.
 *
 * A page is a window the server hands back whole, and the stored list was one
 * of those once, so each is contiguous in itself. Whether the two meet is a
 * question only the server can answer - the one thing that settles it here is
 * a message they have in common.
 *
 * The join is tested at the newest stored message *below the page*, rather
 * than at whatever is newest overall: a message pushed over Top while the page
 * was in flight sits above the hole, and would otherwise vouch for a
 * continuity it knows nothing about.
 *
 * @param prevMessages  The session's stored messages
 * @param nextMessages  A page from the API, oldest first
 * @param since         The message key the page was anchored on, if any
 */
export var pageLeavesAGap = function pageLeavesAGap(prevMessages, nextMessages, since) {
  // An anchored page begins where the caller said the stored list ends, so
  // it continues it by construction - and shares no message with it, which
  // is the very thing read as a hole below.
  if (since) return false;
  if (!List.isList(prevMessages) || prevMessages.isEmpty()) return false;
  if (!List.isList(nextMessages) || nextMessages.isEmpty()) return false;
  var newestInPage = nextMessages.last();
  var storedBelowPage = prevMessages.filter(function (message) {
    return compareChatMessageAge(message, newestInPage) < 0;
  });
  // The page reaches back past everything stored, so there is nothing for it
  // to leave behind.
  if (storedBelowPage.isEmpty()) return false;
  var joiningMessage = storedBelowPage.max(compareChatMessageAge);
  return !nextMessages.some(function (message) {
    return message.get('key') === joiningMessage.get('key');
  });
};

/**
 * The list without the messages older than the given one.
 *
 * Used to cut a stored list loose from messages it cannot be shown to follow.
 * Deliberately not MAX_DISPLAYED_MESSAGES-based: trimming to a fixed count
 * only clears the stranded messages while the page happens to be exactly that
 * long, and would quietly stop working the day the page size changes.
 *
 * @param messages      A list of messages, oldest first
 * @param oldestToKeep  The message to cut at, kept itself
 */
export var dropMessagesOlderThan = function dropMessagesOlderThan(messages, oldestToKeep) {
  return messages.filter(function (message) {
    return compareChatMessageAge(message, oldestToKeep) >= 0;
  });
};

/**
 *  Trim leading and trailing spaces of a string
 * @param text
 */
export var trimTextToEmpty = function trimTextToEmpty(text) {
  if (!String.prototype.trim) {
    return text.replace(/^\s+|\s+$/g, '');
  }
  return text === null || text === void 0 ? void 0 : text.trim();
};

/**
 * @param string             String to format
 * @param replacementString  String that will replace whitespaces
 * @returns {string}         A string where whitespaces, if any, are replaced with instances of replacementString
 */

export var replaceWhitespaces = function replaceWhitespaces(string, replacementString) {
  if (isEmptyNullOrUndefined(string)) {
    return '';
  }
  var formatted = trimTextToEmpty(string);
  return formatted.replace(/\s+/g, replacementString);
};

/**
 * Calculates the number of newlines/linebreaks/rows in a message
 *
 * @param {string} message
 * @returns {number}
 */
export var getLinksFromMessage = function getLinksFromMessage(message) {
  if (!Map.isMap(message) || !message.get('message')) {
    return null;
  }
  var messageParts = applyLink(message.get('message'));
  var isLink = function isLink(part) {
    return part instanceof Object && part && part.props && part.props.href;
  };
  var messageLink = messageParts.find(isLink);
  if (messageLink) {
    return messageLink.props.href;
  } else {
    return null;
  }
};

/**
 * Find if link is a key in the OpenGraph data map.
 * if argument 'link' is not defined, empty or null, then
 * the function returns true.
 * This is so that the OG fetcher doesn't try to fetch OG data
 * for those values.
 */
export var isInOpenGraphData = function isInOpenGraphData(openGraphData, link) {
  if (!link) {
    return true;
  } else if (!Map.isMap(openGraphData)) {
    return false;
  }
  return openGraphData.has(link);
};

/**
 * Given a list of messages, look through them and return any links
 * that are not already in our OG data.
 */
export var getNewLinksFromMessages = function getNewLinksFromMessages(messages, openGraphData) {
  if (!messages) {
    return List();
  }
  return messages.map(getLinksFromMessage).filter(function (link) {
    return !isInOpenGraphData(openGraphData, link);
  }).toSet().toList();
};
export var receivedNewMessages = function receivedNewMessages(oldMessages, newMessages) {
  if (oldMessages.isEmpty()) {
    return !newMessages.isEmpty();
  }
  var oldKey = oldMessages.last().get('key');
  var newKey = newMessages.last().get('key');
  return oldKey !== newKey;
};
export var receivedNewFailedMessages = function receivedNewFailedMessages(oldMessages, newMessages) {
  if (newMessages.isEmpty()) {
    return !oldMessages.isEmpty();
  }
  return receivedNewMessages(oldMessages, newMessages);
};
export var receivedOldMessages = function receivedOldMessages(oldMessages, newMessages) {
  var gotNewMessages = receivedNewMessages(oldMessages, newMessages);
  var messageListsSizeDiff = oldMessages.size !== newMessages.size;
  var slicedTooLongList = slicedOldMessages(oldMessages, newMessages);
  return !gotNewMessages && messageListsSizeDiff && !slicedTooLongList;
};
var slicedOldMessages = function slicedOldMessages(oldMessages, newMessages) {
  return oldMessages.size > newMessages.size;
};
export var lastMessageSentByMe = function lastMessageSentByMe(messages, extensionKey) {
  var lastMessage = !messages.isEmpty() && messages.last();
  var lastMessageExtensionKey = lastMessage && lastMessage.getIn(['from', 'extensionKey']);
  return extensionKey === lastMessageExtensionKey;
};
export var getStyledMessageBody = function getStyledMessageBody(message, sessionKey) {
  var hasMarkdownEnabled = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var msg = message.get('message');
  if (!msg) return '';

  // Only use markdown if feature is enabled and message contains markdown characters
  if (hasMarkdownEnabled && /([*_`#[\]-]|\n)/.test(msg)) {
    return /*#__PURE__*/_jsx("div", {
      className: "markdown max-w-none whitespace-pre-wrap",
      children: /*#__PURE__*/_jsx(ReactMarkdown, {
        remarkPlugins: [remarkGfm, remarkLinkify],
        skipHtml: true,
        components: {
          code: function code(_ref) {
            var inline = _ref.inline,
              children = _ref.children;
            return /*#__PURE__*/_jsx(CodeBlockWithCopy, {
              code: children,
              inline: inline
            });
          },
          a: function a(_ref2) {
            var children = _ref2.children,
              props = _objectWithoutProperties(_ref2, _excluded);
            return /*#__PURE__*/_jsx("a", _objectSpread(_objectSpread({}, props), {}, {
              target: "_blank",
              rel: "noopener noreferrer",
              children: children
            }));
          },
          p: function p(_ref3) {
            var children = _ref3.children;
            return /*#__PURE__*/_jsx("p", {
              className: "leading-6 whitespace-pre-wrap text-black",
              children: children
            });
          },
          input: function input(_ref4) {
            var checked = _ref4.checked,
              type = _ref4.type,
              props = _objectWithoutProperties(_ref4, _excluded2);
            if (type === 'checkbox') {
              return /*#__PURE__*/_jsx(Checkbox, {
                checked: !!checked,
                disabled: true,
                onChange: function onChange() {},
                className: "inline-block align-middle"
              });
            }
            // fallback for other input types
            return /*#__PURE__*/_jsx("input", _objectSpread({
              type: type,
              checked: checked
            }, props));
          }
        },
        children: msg
      })
    });
  }
  var styledMessageBody = applyStyling(msg, message.get('stylings'), sessionKey);
  return processShortEmojiMessage(message === null || message === void 0 ? void 0 : message.get('message'), message === null || message === void 0 ? void 0 : message.get('key'), styledMessageBody);
};
export var processShortEmojiMessage = function processShortEmojiMessage(messageText, messageKey, styledMessageBody) {
  if (!isShortEmojiMessage(messageText)) return styledMessageBody;
  var emojiCount = 0;
  return buildEmojiMessage(styledMessageBody, function (emoji) {
    return /*#__PURE__*/_jsx("span", {
      style: {
        fontSize: '2.5em',
        lineHeight: '1.25em'
      },
      children: emoji
    }, 'emoji-' + messageKey + emojiCount++);
  });
};
export var getStringWithStringInsertedAtPosition = function getStringWithStringInsertedAtPosition(string, stringToInsert, position) {
  if (!stringToInsert && !string) {
    return '';
  }
  if (!stringToInsert) {
    return string;
  }
  if (!string) {
    return stringToInsert;
  }
  if (isNaN(position) || position < 0) {
    //Position is either not a number or a negative value, adding stringToInsert to the end of string
    return string + stringToInsert;
  }
  return [string.slice(0, position), stringToInsert, string.slice(position)].join('');
};
export var ChatContent = Object.freeze({
  FILES: 'files',
  POSTS: 'posts',
  IMAGES: 'images',
  UNKNOWN: 'unknown'
});
export var getFormattedDateTime = function getFormattedDateTime(t, i18next) {
  if (!t) {
    return '-';
  }
  var time = moment(t);
  var startOfDay = moment(t).startOf('day');
  var today = moment();
  var isWithinWeek = startOfDay.diff(today.startOf('day'), 'days') > -7;
  var isWithinYear = startOfDay.diff(today.startOf('day'), 'years') === 0;
  if (isWithinWeek) {
    return time.locale(i18next.language).format('ddd HH:mm');
  } else if (isWithinYear) {
    return time.locale(i18next.language).format('D MMM HH:mm');
  } else {
    return time.locale(i18next.language).format('D MMM YYYY HH:mm');
  }
};
export var fileSizeConverter = function fileSizeConverter(bytes) {
  var decimals = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  if (!bytes) return undefined;
  if (bytes === 0) return '0 Bytes';
  var k = 1024;
  var dm = decimals < 0 ? 0 : decimals;
  var sizes = ['Bytes', 'KB', 'MB', 'GB'];
  var i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};
export var ChatAttachments = Object.freeze({
  DOC: 'doc',
  KEYNOTE: 'keynote',
  MOVIE: 'movie',
  NUMBERS: 'numbers',
  PAGES: 'pages',
  PDF: 'pdf',
  PHOTO: 'photo',
  PPT: 'ppt',
  SKETCH: 'sketch',
  XLS: 'xls',
  ZIP: 'zip',
  DEFAULT: 'default',
  UNKNOWN: 'unknown'
});
export var getAttachmentTypeFromExtension = function getAttachmentTypeFromExtension(fileName) {
  if (!fileName) {
    return ChatAttachments.UNKNOWN;
  }
  var filename = fileName.toLowerCase();
  if (filename.match(/.*\.doc/i) || filename.match(/.*\.docx/i)) {
    return ChatAttachments.DOC;
  } else if (filename.match(/.*\.key/i)) {
    return ChatAttachments.KEYNOTE;
  } else if (filename.match(/.*\.mp4/i) || filename.match(/.*\.mov/i)) {
    return ChatAttachments.MOVIE;
  } else if (filename.match(/.*\.numbers/i)) {
    return ChatAttachments.NUMBERS;
  } else if (filename.match(/.*\.pages/i)) {
    return ChatAttachments.PAGES;
  } else if (filename.match(/.*\.pdf/i)) {
    return ChatAttachments.PDF;
  } else if (filename.match(/.*\.ico/i) || filename.match(/.*\.jpg/i) || filename.match(/.*\.png/i) || filename.match(/.*\.svg/i) || filename.match(/.*\.icns/i) || filename.match(/.*\.jpeg/i) || filename.match(/.*\.heic/i)) {
    return ChatAttachments.PHOTO;
  } else if (filename.match(/.*\.ppt/i) || filename.match(/.*\.pptx/i)) {
    return ChatAttachments.PPT;
  } else if (filename.match(/.*\.sketch/i)) {
    return ChatAttachments.SKETCH;
  } else if (filename.match(/.*\.xls/i) || filename.match(/.*\.xlsx/i)) {
    return ChatAttachments.XLS;
  } else if (filename.match(/.*\.zip/i)) {
    return ChatAttachments.ZIP;
  } else if (filename.match(/^([^\\]*)\.(\w+)$/)) {
    return ChatAttachments.DEFAULT;
  } else {
    return ChatAttachments.UNKNOWN;
  }
};
export var getImageFromChatAttachment = function getImageFromChatAttachment() {
  var attachment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ChatAttachments.UNKNOWN;
  switch (attachment) {
    case ChatAttachments.MOVIE:
      return 'movie';
    case ChatAttachments.PHOTO:
      return 'image';
    default:
      return 'file';
  }
};

/**
 * Generic hash function that should be used by all apps.
 */
var hashCode = function hashCode(ext) {
  var k = 32952;
  for (var i = 0; i < ext.length; i++) {
    k = (k << 5) + k + ext.charCodeAt(i);
  }
  return k;
};
var getColorFromFileExtension = function getColorFromFileExtension(fileName) {
  var fileSplit = fileName.match(/^([^\\]*)\.(\w+)$/);
  var extension = fileSplit[fileSplit.length - 1];
  var hash = hashCode(extension);
  return getColorFromInt(hash);
};
export var getColorFromChatAttachment = function getColorFromChatAttachment() {
  var attachment = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : ChatAttachments.UNKNOWN;
  var fileName = arguments.length > 1 ? arguments[1] : undefined;
  switch (attachment) {
    case ChatAttachments.DOC:
    case ChatAttachments.KEYNOTE:
      return getBrand().theme.palette.flavors.flavor6;
    case ChatAttachments.MOVIE:
      return getBrand().theme.palette.flavors.flavor8;
    case ChatAttachments.NUMBERS:
    case ChatAttachments.XLS:
      return getBrand().theme.palette.flavors.green;
    case ChatAttachments.PAGES:
    case ChatAttachments.ZIP:
      return getBrand().theme.palette.flavors.flavor2;
    case ChatAttachments.PDF:
    case ChatAttachments.PPT:
      return getBrand().theme.palette.flavors.red;
    case ChatAttachments.PHOTO:
    case ChatAttachments.SKETCH:
      return getBrand().theme.palette.flavors.flavor1;
    case ChatAttachments.UNKNOWN:
      return getBrand().theme.palette.greyscale.greyLight;
    default:
      return getColorFromFileExtension(fileName);
  }
};
export var getMuteLengthEnum = function getMuteLengthEnum(activeProfileEndTime, i18next, statusText) {
  var TimeLengthEnum = {
    ONE_MINUTE: {
      value: '1',
      label: "1 ".concat(i18next.t('duration.hour'))
    },
    EIGHT_HOURS: {
      value: '8',
      label: "8 ".concat(i18next.t('duration.hours'))
    },
    TWENTY_FOUR_HOURS: {
      value: '24',
      label: "24 ".concat(i18next.t('duration.hours'))
    }
  };
  if (activeProfileEndTime) {
    TimeLengthEnum.UNTIL_PROFILE_ACTIVE = {
      value: 'profile',
      label: i18next.t('Chat.Mute.UntilProfile', {
        ProfileStatus: statusText
      })
    };
  }
  TimeLengthEnum.UNTIL_FURTHER_NOTICE = {
    value: null,
    label: i18next.t('Chat.Mute.UntilForever')
  };
  return TimeLengthEnum;
};
export var getMutedUntil = function getMutedUntil(chatSession) {
  return chatSession.getIn(['chatMuted', 'mutedUntil']);
};
export var getProxyImageUrl = function getProxyImageUrl(imageUrl) {
  return homeActionUrlPath("/api/internal/opengraph/image?url=".concat(encodeURIComponent(imageUrl)));
};

/**
 * Go through the whole text backwards and return a list of mentions
 */
export var createMentionDTOsFromText = function createMentionDTOsFromText(text, extensions) {
  var mentions = List();
  var currentMentionPosition = text.length;
  for (;;) {
    // Find a possible mention
    var regexp = new RegExp(REGEXP_POSSIBLE_MENTION_STRICT);
    var regexpResult = regexp.exec(text);
    if (regexpResult == null) {
      return mentions;
    }
    var possibleMention = regexpResult[1];
    currentMentionPosition -= possibleMention.length;
    // Go through the possible mention, one more character at a time
    var _loop = function _loop() {
        var filterText = possibleMention.substring(1, i);
        var filteredExtensions = extensions.filter(function (extension) {
          return mentionFilter(extension, filterText);
        });
        if (filteredExtensions.size > 1) {
          // If there are more than two possible matches, continue by adding another character
        } else if (filteredExtensions.size === 0) {
          // If there are no matches, break and try to find another possible mention
          return 0; // break
        } else {
          var extension = filteredExtensions.first();
          var name = extractName(extension.get('contact'), '');
          if (name.length !== filterText.length) {
            return 1; // continue
          }
          if (name !== filterText) {
            return 0; // break
          }
          var key = extension.get('chatUserKey');
          var length = name.length + 1;
          var mentionDTO = {
            startPosition: currentMentionPosition,
            length: length,
            stylingReference: {
              stylingType: 'chatuser',
              chatUser: {
                key: key
              }
            }
          };
          mentions = mentions.push(fromJS(mentionDTO));
        }
      },
      _ret;
    for (var i = 2; i <= possibleMention.length; i++) {
      _ret = _loop();
      if (_ret === 0) break;
      if (_ret === 1) continue;
    }
    text = text.substring(0, currentMentionPosition);
  }
};
var mentionFilter = function mentionFilter(extension, filterText) {
  if (!extension) return false;
  var firstName = extension.getIn(['contact', 'firstName'], '');
  var lastName = extension.getIn(['contact', 'lastName'], '');
  var fullName = firstName + ' ' + lastName;
  return new RegExp('(^|\\s)' + escapeRegExp(filterText), 'i').test(fullName);
};
export var isTwoPersonSMSChat = function isTwoPersonSMSChat(attachment, session, extensions) {
  if (attachment || !session) {
    return false;
  }
  var members = session.get('members', List()).filter(function (member) {
    return member.get('contact');
  });
  var memberExtensionKeys = members.map(function (member) {
    return member.get('extensionKey');
  }).toSet();
  var filteredExtensions = extensions.filter(function (extension) {
    return memberExtensionKeys.contains(extension.get('key')) && extensionHasMobile(extension);
  });
  return filteredExtensions.size === 2;
};
export var extensionHasMobile = function extensionHasMobile(extension) {
  var contact = extension.get('contact');
  return contact && contact.get('mobileNumber');
};
export var getChatSessionMemberExtensions = function getChatSessionMemberExtensions(extensions, chatSession, loggedInEntityKey) {
  var sessionMembersKeys = chatSession.get('members', List()).map(function (member) {
    return member.get('extensionKey');
  });
  return extensions.filter(function (ext) {
    return sessionMembersKeys.contains(ext.get('key')) && ext.get('key') !== loggedInEntityKey;
  }, Map());
};
export var copyImage = function copyImage(url) {
  // Use the Canvas API, which seems to offer the best cross-browser chance of this
  // working out. We want the full original image to be copied, so create a throwaway
  // Image tag to obtain that.
  var img = new Image();
  img.src = url;
  img.onload = /*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var canvas, ctx;
    return _regeneratorRuntime.wrap(function (_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          canvas = document.createElement('canvas');
          ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0, img.width, img.height);
          canvas.toBlob(/*#__PURE__*/function () {
            var _ref6 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(blob) {
              return _regeneratorRuntime.wrap(function (_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    _context.next = 1;
                    return navigator.clipboard.write([new ClipboardItem(_defineProperty({}, blob.type, blob))]);
                  case 1:
                  case "end":
                    return _context.stop();
                }
              }, _callee);
            }));
            return function (_x) {
              return _ref6.apply(this, arguments);
            };
          }());
        case 1:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
};